---
title: Merging
lede: Land the ticked files on your computer as one commit you author, or as plain files.
order: 27
part: Review and merge
---

Merging brings the session's ticked files into your own repositories. Open it with **Merge `n` files** on the review footer, **Session › Merge…** (**Merge into `<branch>`…** once the branch is settled), or `⌘⇧↩`.

![The merge dialog](media/merge-modal.webp)

### Lands as

The dialog first asks how the work lands, for the whole session:

- **A commit on a branch** — one commit in each repository.
- **Files in the projects** — the files written into your working trees, with no git at all.

### A commit on a branch

1. Name the **Branch**. One branch answers for every repository; pick an existing local branch or type a new name to create it.
2. Write the **Title**, and a **Description** if you want one.
3. Check the **Repositories** rows. Each says where a push would go, or why it cannot take the merge yet.
4. Choose whether to push (see below), then press **Merge**.

The field starts empty every time, except in a session started from a pull request, where it starts on that pull request's branch. After the first merge, the branch is settled: later merges from this session join it.

### One commit per merge

Each merge makes exactly one commit per repository, authored with your git identity. Whatever the agent committed along the way is folded into it — no merge commit, no intermediate commits.

If the branch has not moved since the session was cut, the commit is fast-forwarded onto it. If it has, the session's changes are squashed onto its tip. Either way, the next sync has nothing to replay.

The merge is built in a temporary worktree with git hooks off, so a failed merge never leaves your checkout half-done. A branch with a remote is refreshed from it first; if your local and remote copies have both moved, the merge is refused until you reconcile them.

### Branch, title and description

Both message fields start empty, or with the message of this session's previous merge. **Fill with Agent**, above the fields, asks the agent, in its terminal, to write them — and to name the branch too while the Branch field is still empty; the fields fill when it answers, and you can still edit them. Fill with Agent is refused while the agent is working.

### Branch and commit formats

**Workspace settings › Git & merge › Branch name format** and **Commit message format** set your team's conventions, each picked from a preset or written as a template:

| Branch presets | Example |
|---|---|
| Type / ticket-description | `feature/PROJ-123-add-github-login` |
| Type / description | `feature/add-github-login` |
| Ticket-description | `PROJ-123-add-github-login` |
| User / description | `jdoe/add-github-login` |

| Commit presets | Example |
|---|---|
| Conventional Commits | `feat(auth): add sign-in with GitHub` |
| Conventional Commits + ticket | `feat(auth): PROJ-123 add sign-in with GitHub` |
| Ticket key first | `PROJ-123 add sign-in with GitHub` |
| Gitmoji | `✨ add sign-in with GitHub` |
| Plain imperative (50/72) | `add sign-in with GitHub` |

A custom template uses `{type}`, `{scope}`, `{ticket}`, `{description}`, `{user}` and `{emoji}`; a part in `[ ]` is left out when it has no value, so Conventional Commits reads `{type}[({scope})][!]: {description}`. Each format can carry a few words for the agent — the types or scopes you use.

Fill with Agent writes in these formats, the session's agent is told them when it starts, and a branch named by itself on landing follows the branch format. In the merge dialog, a branch name or title off the format is flagged under its field in yellow; it is never refused.

**Settings › Git & merge › Credit the agent in the commit** adds a `Co-authored-by` trailer naming the agent. It is off by default.

![Git and merge settings](media/settings-git.webp)

### Push and pull request

Two switches under the repositories, both on by default:

| Switch | What it does |
|---|---|
| **Push after merging** | Pushes the branch to each repository's remote as soon as the merge lands. |
| **Open a pull request after pushing** | Opens a pull request from that branch, with your title and description. Turning it on turns the push on. |

The pull request switch is not offered when you merge into the branch the session was cut from, for a remote on no git provider, or in a session started from a pull request — its push updates that one. Repositories with no remote say *Committed as you, on this computer*.

### Files in the projects

The ticked files are written into your working trees as the session has them. Nothing is staged, committed or moved; commit them later in your own tools. The button reads **Write**.

:::warn Uncommitted changes are protected
A repository whose working tree already has uncommitted changes to one of those files is refused as a whole, naming the files. It says *Modified on your computer*, which refreshing the review does not clear. Commit, stash or revert them, then write again.
:::

The session stays open, and files leave the review only from repositories written in full.

### When the session moved after you read it

The dialog says beside its button when the review last changed, reads it again when it opens, and lists any file that moves while it is open, its button then reading **Merge these changes**. What lands is what the dialog had on screen when you pressed **Merge**. If the session has changed since, nothing lands: the dialog counts and lists the files, and its button becomes **Refresh the review and retry**. That reads the review again and shows what moved, still landing nothing; press **Merge with these changes** once you have read them, or **Read them** to go back to the review.

### What blocks a merge

- No file ticked.
- A sync stopped on conflicts — finish or abandon it first. See [Sync and conflicts](/sync).
- A repository that cannot take the branch: a name git refuses, or the branch checked out elsewhere with uncommitted changes.
- A broken rule, or an audit your organization asks for — see [Rules](/rules).
- A finding left open or left to you on the map, unless you ignored it — see [The Change Map](/change-map).

### After it lands

The review shows the receipt and the **Push** block: one row per repository with where it goes and whether it is up to date, **Push** on each row, and **Push all** above them. A refused push stays on its row; press **Push again** once fixed. **Open pull request** appears once a branch is pushed. **Session › Push** does the same.

Airlock never force-pushes. With the push off, nothing leaves your computer, and the merge is a local commit you can undo like any other.

### Landing by itself

**Settings › Git & merge › When a session is finished** can land a session without you once the agent has handed back and nothing is left to run. A workspace can answer it for itself. Each step is off by default and needs the one before it:

| Setting | What it does |
|---|---|
| **Name the branch** | Lands on a new branch named `airlock/<title>-<id>`, or the one the session already names |
| **Write the commit and merge** | Asks the agent for the message, as **Fill with Agent** does, and merges |
| **Push** | Pushes the landed branch |
| **Open the pull request** | Against the branch the session was cut from (GitHub only) |
| **Then the session** | **Stays open**, **Stopped**, or **Closed** |

The run stops at the first refusal and the review says why; what landed stays landed.

### Running a task again

**Run again**, on an archived task, starts a new task with the same title, prompt, workflow, repositories and branches. See [Archive](/archive).
